<template>
  <transition name="fade" mode="out-in">
    <div v-if="!validatingAddForm" id="type_actions">
      <!-- Form buttons -->
      <div v-if="!hideButtons && !carouselOptions" class="normal-buttons">
        <transition-group name="fade">
          <div key="tasks"><action-task
            :id="id"
            :actionButtonTasks="actionButtonTasks"
            :requestObject="requestObject"
            :definitionContent="definitionContent"
            :visualDefinition="visualDefinition"
            :type="type"
            :validateForm="validateForm"
            :validateAddForm="validateAddForm"
            :languageSelected="languageSelected"
            :definitionContentId="definitionContentId"
            :definitionContentCode="definitionContentCode"
            :requestObjectForAddModified="requestObjectForAddModified"
            :language="language"
            :authorization="authorization"
            :visuals="visuals"
            :apiparams="apiparams"
            :formAddErrors="formAddErrors"
            :formAddSaved="formAddSaved"
            :formAddRemoved="formAddRemoved"
            @refreshDefinition="refreshDefinition"
            @repeatableGroupModified="modifyDefinitionContentRequestObject"
            @activateValidatingAddForm="activateValidatingAddForm"
            @deactivateValidatingAddForm="deactivateValidatingAddForm"
            @errorAddForm="errorAddForm"
            @errorModifyForm="errorModifyForm"
            @formAddSaved="addSaved"
            @newLanguageSelected="newLanguageSelected"
            @modifyRepeatibleGroup="modifyDefinitionContentRequestObject"
            @modifyingAddForm="modifyingAddForm"
            @removingAddForm="removingAddForm"
            @conditionalDefinitionChange="conditionalDefinitionChange"
            @refreshDefinitionContent="refreshDefinitionContent"
            @waitingProcessInit="waitingProcessInit"
            @taskLink="taskLink"
            @editingQuestion="editingQuestion"
            @callSaveByFieldChange="callSaveByFieldChange"
          /></div>
          <div class="buttons w3-twothird" key="buttons">
            <!-- Delete buttons -->
            <button v-if="definitionContentId && actions.delete" v-on:click="callDelete" class="w3-btn w3-round-large delete" :disabled="validating">
              <transition name="fade" mode="out-in">
                <span v-if="!validating"><i :class="getDeleteIcon(actions.delete.text)"></i> {{ deleteButtonText }}</span>
                <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' />
              </transition>
            </button>
            <transition name="fade" mode="out-in">
              <div class="containerFormConfirmDelete" v-if="formConfirmDelete">
                <button class="w3-btn w3-round-large btnConfirmDelete" v-on:click="confirmDelete">
                  <transition name="fade" mode="out-in">
                    <div v-if="!validatingDelete" class="no_spinner">
                      <i class="sherpa-icon icon-ico_ok"></i>
                      <span> {{ getLang('yes') }} </span>
                    </div>
                    <spinner-loading v-else class="spinner-loading" :line-size='2' line-fg-color='#5dbecd' />
                  </transition>
                </button>
                <button class="w3-btn w3-round-large btnConfirmDelete" v-on:click="cancelDelete">
                  <transition name="fade" mode="out-in">
                    <div v-if="!validatingDelete" class="no_spinner">
                      <i class="sherpa-icon icon-ico_error"></i>
                      <span> {{ getLang('no') }} </span>
                    </div>
                  </transition>
                </button>
                <span class="formConfirmDelete"><i class="sherpa-icon icon-question"></i> {{ getLang('formConfirmDelete') }} </span>
              </div>
            </transition>

            <!-- Save buttons -->
            <transition name="fade" mode="out-in">
              <button v-if="actions.create && !formConfirmDelete" v-on:click="callCreate(true)" class="w3-btn w3-round-large save-and-stay" :disabled="validating">
                <transition name="fade" mode="out-in">
                  <span v-if="!validating">
                    <i :class="getSaveIcon(actions.create.text)"></i>
                    <template v-if="actions.create.callback">
                      {{ getLang('saveAndStay', actions.create.text) }}
                    </template>
                    <template v-else>
                      {{ getLang('saveAndNew', actions.create.text) }}
                    </template>
                  </span>
                  <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' />
                </transition>
              </button>
            </transition>
            <transition name="fade" mode="out-in">
              <button v-if="actions.create && !formConfirmDelete" v-on:click="callCreate(false)" class="w3-btn w3-round-large save-and-out" :disabled="validating">
                <transition name="fade" mode="out-in">
                  <span v-if="!validating"><i :class="getSaveIcon(actions.create.text)"></i> {{ getLang('save', actions.create.text) }}</span>
                  <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' />
                </transition>
              </button>
            </transition>

            <!-- Update buttons -->
            <transition name="fade" mode="out-in">
              <button v-if="definitionContentId && actions.update && !formConfirmDelete" v-on:click="callUpdate(true)" class="w3-btn w3-round-large update-and-stay" :disabled="validating">
                <transition name="fade" mode="out-in">
                  <span v-if="!validating"><i :class="getSaveIcon(actions.update.text)"></i> {{ getLang('saveAndStay', actions.update.text) }}</span>
                  <spinner-loading  class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' />
                </transition>
              </button>
            </transition>
            <transition name="fade" mode="out-in">
              <button v-if="definitionContentId && actions.update && !formConfirmDelete" v-on:click="callUpdate(false)" class="w3-btn w3-round-large update-and-out" :disabled="validating">
                <transition name="fade" mode="out-in">
                  <span v-if="!validating"><i :class="getSaveIcon(actions.update.text)"></i> {{ getLang('save', actions.update.text) }}</span>
                  <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' />
                </transition>
              </button>
            </transition>
          </div>
          <!-- Form messages -->
          <div key="message"><action-message
            :language="language"
            :formErrors="formErrors"
            :formAddErrors="formAddErrors"
            :formModifyErrors="formModifyErrors"
            :formSaved="formSaved"
            :formAddSaved="formAddSaved"
            :formSavedError="formSavedError"
            :formSavedErrorMsg="formSavedErrorMsg"
            :formDeleted="formDeleted"
            :resultSuccessMsg="resultSuccessMsg"
            :authorization="authorization"
            :visuals="visuals"
            :apiparams="apiparams"
            :visualDefinition="visualDefinition"
            @popupClose="popupClose"
          /></div>
        </transition-group>
      </div>

      <div v-else-if="!hideButtons && carouselOptions">
        <div class="carousel-buttons w3-col">
          <div v-if="showPrevButton" class="button prev">
            <button :class="['prev', {'disabled': disabledPrev}]" :disabled="disabledPrev" v-on:click="callPrevCarouselPage()">
              <span>{{ getLang('previousStep') }}</span>
            </button>
          </div>
          <div v-if="showSectionsPagination" :class="['pagination-sections']">
            <div v-for="(reqDef, index) in requestDefinition"
              :class="['pagination-section', {'current-pagination': index === carouselPage}, {'next-pagination': index > carouselPage}, {'past-pagination': index < carouselPage}]"
              :key="reqDef.id"
              :style="{width: 100 / slideCount + '%'}"
              @click="carouselGoToPage(index)"
            >
              <div class="pagination-top-separator"></div>
              <div class="pagination-icon">
                <transition name="fade" mode="out-in">
                  <svg v-if="index === carouselPage" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 0.25C3.71978 0.25 0.25 3.71978 0.25 8C0.25 12.2802 3.71978 15.75 8 15.75C12.2802 15.75 15.75 12.2802 15.75 8C15.75 3.71978 12.2802 0.25 8 0.25ZM10.5 8C10.5 9.3785 9.3785 10.5 8 10.5C6.6215 10.5 5.5 9.3785 5.5 8C5.5 6.6215 6.6215 5.5 8 5.5C9.3785 5.5 10.5 6.6215 10.5 8Z" fill="#4F4F4F"/>
                  </svg>
                  <svg v-else-if="index > carouselPage" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 0.25C3.71875 0.25 0.25 3.71875 0.25 8C0.25 12.2812 3.71875 15.75 8 15.75C12.2812 15.75 15.75 12.2812 15.75 8C15.75 3.71875 12.2812 0.25 8 0.25ZM8 14.25C4.54688 14.25 1.75 11.4531 1.75 8C1.75 4.54688 4.54688 1.75 8 1.75C11.4531 1.75 14.25 4.54688 14.25 8C14.25 11.4531 11.4531 14.25 8 14.25Z" fill="#E0E0E0"/>
                  </svg>
                  <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.75 8C15.75 12.2802 12.2802 15.75 8 15.75C3.71978 15.75 0.25 12.2802 0.25 8C0.25 3.71978 3.71978 0.25 8 0.25C12.2802 0.25 15.75 3.71978 15.75 8ZM7.10356 12.1036L12.8536 6.35356C13.0488 6.15831 13.0488 5.84172 12.8536 5.64647L12.1465 4.93937C11.9512 4.74409 11.6346 4.74409 11.4393 4.93937L6.75 9.62869L4.56066 7.43934C4.36541 7.24409 4.04881 7.24409 3.85353 7.43934L3.14644 8.14644C2.95119 8.34169 2.95119 8.65828 3.14644 8.85353L6.39644 12.1035C6.59172 12.2988 6.90828 12.2988 7.10356 12.1036Z" fill="#27A556"/>
                  </svg>
                </transition>
              </div>
              <span class="pagination-name">{{ reqDef.label }}</span>
            </div>
          </div>
          <div class="button next">
            <transition name="fade" mode="out-in">
              <button v-if="!lastButton" :class="['next']" v-on:click="callNextCarouselPage()">
                <span>{{ getLang('nextStep') }}</span>
              </button>
              <button v-else v-on:click="callCreate(false)" class="save-and-out" :disabled="validating">
                <transition name="fade" mode="out-in">
                  <span v-if="!validating">{{ getLang('save', actions.create.text) }}</span>
                  <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' />
                </transition>
              </button>
            </transition>
          </div>
        </div>

        <!-- Form messages -->
        <action-message
          :language="language"
          :formErrors="formErrors"
          :formAddErrors="formAddErrors"
          :formModifyErrors="formModifyErrors"
          :formSaved="formSaved"
          :formAddSaved="formAddSaved"
          :formSavedError="formSavedError"
          :formSavedErrorMsg="formSavedErrorMsg"
          :formDeleted="formDeleted"
          :resultSuccessMsg="resultSuccessMsg"
          :authorization="authorization"
          :visuals="visuals"
          :apiparams="apiparams"
          @popupClose="popupClose"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import language from '../language'
import ActionMessage from './message'
import ActionTask from './actionTask'
import SpinnerLoading from '@/components/shared/SpinnerLoading.vue'

export default {
  name: 'action-buttons',
  props: [
    'id',
    'actions',
    'definitionContentId',
    'validating',
    'validatingAddForm',
    'formErrors',
    'formAddErrors',
    'formModifyErrors',
    'formSaved',
    'resultSuccessMsg',
    'formAddSaved',
    'formSavedError',
    'formDeleted',
    'formConfirmDelete',
    'validatingDelete',
    'formSavedErrorMsg',
    'language',
    'authorization',
    'type',
    'visuals',
    'apiparams',
    'hideButtons',
    'menuName',
    'carouselOptions',
    'carouselPage',
    'slideCount',
    'requestDefinition',
    'requestObject',
    'definitionContent',
    'objectChanged',
    'visualDefinition',
    'validateForm',
    'validateAddForm',
    'languageSelected',
    'definitionContentCode',
    'requestObjectForAddModified',
    'formAddRemoved',
    'actionButtonTasks'
  ],
  data () {
    return {
    }
  },
  components: {
    SpinnerLoading,
    ActionMessage,
    ActionTask
  },
  methods: {
    callDelete () {
      this.$emit('callDelete', true, this.actions.delete.confirmText)
    },
    callCreate (stayAfterSave) {
      this.$emit('callCreate', stayAfterSave, null, this.actions.create.confirmText)
    },
    callUpdate (stayAfterSave) {
      this.$emit('callUpdate', stayAfterSave, null, this.actions.update.confirmText)
    },
    popupClose () {
      this.$emit('popupClose')
    },
    confirmDelete () {
      this.$emit('confirmDelete')
    },
    cancelDelete () {
      this.$emit('cancelDelete')
    },
    getLang (key, translatedText) {
      var result = ''
      if (translatedText) {
        if (this.confirmEnding(key, 'AndStay')) {
          result = translatedText + ' ' + language(this.language, 'andStay')
        } else {
          result = translatedText
        }
      } else {
        result = language(this.language, key)
      }
      return result
    },
    confirmEnding (string, target) {
      if (string.substr(-target.length) === target) {
        return true
      } else {
        return false
      }
    },
    callPrevCarouselPage () {
      this.$emit('callPrevCarouselPage')
    },
    callNextCarouselPage () {
      this.$emit('callNextCarouselPage')
    },
    carouselGoToPage (page) {
      if (page < this.carouselPage) {
        this.$emit('callPaginationCarouselPage', page)
      }
    },
    getSaveIcon (text) {
      var result

      if (text) {
        result = 'sherpa-icon icon-save-' + this.id
      } else {
        result = 'far fa-save'
      }

      return result
    },
    getDeleteIcon (text) {
      var result

      if (text) {
        result = 'sherpa-icon icon-delete-' + this.id
      } else {
        result = 'sherpa-icon icon-trash'
      }

      return result
    },
    refreshDefinition (newReqObj, scope) {
      this.$emit('refreshDefinition', newReqObj, scope)
    },
    modifyDefinitionContentRequestObject (object, callUpdate, code) {
      this.$emit('repeatableGroupModified', object, callUpdate, code)
    },
    activateValidatingAddForm (id, code) {
      this.$emit('activateValidatingAddForm', id, code)
    },
    deactivateValidatingAddForm () {
      this.$emit('deactivateValidatingAddForm')
    },
    errorAddForm (formAddErrors) {
      this.$emit('errorAddForm', formAddErrors)
    },
    errorModifyForm (formModifyErrors) {
      this.$emit('errorModifyForm', formModifyErrors)
    },
    addSaved (code) {
      this.$emit('formAddSaved', code)
    },
    newLanguageSelected (language) {
      this.$emit('newLanguageSelected', language)
    },
    modifyingAddForm (code) {
      this.$emit('modifyingAddForm', code)
    },
    removingAddForm (code, id) {
      this.$emit('removingAddForm', code, id)
    },
    conditionalDefinitionChange (conditionalFieldId, newReqObj) {
      this.$emit('conditionalDefinitionChange', conditionalFieldId, newReqObj)
    },
    refreshDefinitionContent () {
      this.$emit('refreshDefinitionContent')
    },
    waitingProcessInit () {
      this.$emit('waitingProcessInit')
    },
    taskLink (obj) {
      this.$emit('taskLink', obj)
    },
    editingQuestion (editingQuestion) {
      this.$emit('editingQuestion', editingQuestion)
    },
    callSaveByFieldChange (alreadyValidated) {
      this.$emit('callSaveByFieldChange', alreadyValidated)
    }
  },
  computed: {
    deleteButtonText () {
      var result
      result = this.getLang('delete', this.actions.delete.text)
      if (this.menuName) {
        // result += ' ' + this.menuName
      }
      return result
    },
    disabledPrev () {
      return this.carouselPage === 0
    },
    showPrevButton () {
      return this.carouselOptions && this.carouselOptions.showPrevButton
    },
    showSectionsPagination () {
      return this.carouselOptions && this.carouselOptions.showSectionsPagination
    },
    lastButton () {
      return this.carouselPage === this.slideCount - 1
    }
  }
}
</script>

<style lang="scss">
</style>
